<template>
  <main dir="rtl" class="flex-1 pb-8">
    <div class="mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 class="text-2xl font-bold text-gray-900">إدارة جداول الجلسات</h2>
        <div class="flex gap-2">
          <button
            @click="loadSchedules"
            class="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg"
          >
            تحديث
          </button>
          <button
            v-if="canCreate"
            @click="openCreateModal"
            class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
          >
            إنشاء جدول الجلسات
          </button>
        </div>
      </div>

      <div class="bg-white rounded-lg shadow p-4 mb-6">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            v-model="filters.q"
            type="text"
            placeholder="بحث بالعنوان أو الوصف"
            class="w-full p-2 border border-gray-300 rounded-md"
          />
          <select v-model="filters.is_active" class="w-full p-2 border border-gray-300 rounded-md">
            <option value="">كل الحالات</option>
            <option value="true">نشط</option>
            <option value="false">غير نشط</option>
          </select>
          <button
            @click="loadSchedules"
            class="bg-primary hover:bg-primary/90 text-white rounded-md px-4 py-2"
          >
            تطبيق الفلتر
          </button>
        </div>
      </div>

      <div v-if="loading" class="text-center py-8 text-gray-600">جاري التحميل...</div>

      <div v-else class="bg-white rounded-lg shadow overflow-x-auto mb-6">
        <table class="min-w-full">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">العنوان</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">الفترة</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">النوع</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">التسعير</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">الحالة</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">معلمين</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">أيام</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">إجراءات</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            <tr
              v-for="schedule in schedules"
              :key="schedule.id"
              class="hover:bg-gray-50 cursor-pointer"
              @click="selectSchedule(schedule)"
            >
              <td class="px-4 py-3 text-sm font-medium text-gray-800 text-center">{{ schedule.title }}</td>
              <td class="px-4 py-3 text-sm text-gray-600 text-center">{{ formatDate(schedule.start_date) }} - {{ formatDate(schedule.end_date) }}</td>
              <td class="px-4 py-3 text-sm text-gray-600 text-center">{{ scheduleTypeLabel(schedule.schedule_type) }}</td>
              <td class="px-4 py-3 text-sm text-center">
                <span :class="schedule.is_paid ? 'text-blue-700 bg-blue-100' : 'text-green-700 bg-green-100'" class="px-2 py-1 rounded text-xs">
                  {{ schedule.is_paid ? 'مدفوع' : 'مجاني' }}
                </span>
              </td>
              <td class="px-4 py-3 text-sm text-center">
                <span :class="schedule.is_active ? 'text-green-700 bg-green-100' : 'text-red-700 bg-red-100'" class="px-2 py-1 rounded text-xs">
                  {{ schedule.is_active ? 'نشط' : 'غير نشط' }}
                </span>
              </td>
              <td class="px-4 py-3 text-sm text-gray-600 text-center">{{ schedule.teachers?.length || 0 }}</td>
              <td class="px-4 py-3 text-sm text-gray-600 text-center">{{ schedule.days?.length || 0 }}</td>
              <td class="px-4 py-3 text-sm">
                <div class="flex justify-center gap-2">
                  <button
                    v-if="canUpdate"
                    @click.stop="openEditModal(schedule)"
                    class="text-blue-700 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded"
                  >
                    تعديل
                  </button>
                  <button
                    v-if="canDelete"
                    @click.stop="deleteSchedule(schedule)"
                    class="text-red-700 bg-red-50 hover:bg-red-100 px-3 py-1 rounded"
                  >
                    حذف
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="schedules.length === 0">
              <td colspan="8" class="px-4 py-6 text-center text-gray-500">لا توجد جداول الجلسات حالياً</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="selectedSchedule" class="bg-white rounded-lg shadow mb-6">
        <div class="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-gray-200">
          <div>
            <h3 class="text-lg font-semibold text-gray-900">شبكة مجموعات الفترات</h3>
            <p class="text-xs text-gray-500 mt-1">
              كل صف يمثل «ترتيب» واحدًا، وهو الرقم الذي يربط نفس الفترة عبر أيام الأسبوع.
            </p>
          </div>
          <div class="flex flex-wrap items-center gap-2 text-xs">
            <span class="px-2 py-1 rounded bg-gray-100 text-gray-700">
              {{ slotGroupRows.length }} ترتيب
            </span>
            <span
              v-if="incompleteSlotGroupCount"
              class="px-2 py-1 rounded bg-amber-100 text-amber-800"
              title="مجموعات لا تغطي كل أيام الجدول"
            >
              {{ incompleteSlotGroupCount }} غير مكتمل
            </span>
            <span
              v-if="inconsistentSlotGroupCount"
              class="px-2 py-1 rounded bg-amber-100 text-amber-800"
              title="مجموعات تختلف فيها الفترات بالاسم أو التوقيت أو حالة التفعيل"
            >
              {{ inconsistentSlotGroupCount }} غير متطابق
            </span>
          </div>
        </div>

        <div v-if="!sortedSelectedScheduleDays.length" class="px-4 py-6 text-center text-gray-500">
          لا توجد أيام في هذا الجدول
        </div>
        <div v-else-if="!slotGroupRows.length" class="px-4 py-6 text-center text-gray-500">
          لا توجد فترات في هذا الجدول
        </div>
        <div v-else class="overflow-x-auto">
          <!-- border-separate keeps the sticky first column painting correctly; with
               Tailwind's default border-collapse the row borders would not stick. -->
          <table class="min-w-full border-separate border-spacing-0">
            <thead class="bg-gray-50">
              <tr>
                <th
                  class="sticky right-0 z-20 bg-gray-50 border-b border-gray-200 px-4 py-3 text-right text-xs font-medium text-gray-500 min-w-[230px]"
                >
                  الترتيب / الفترة
                </th>
                <th
                  v-for="day in sortedSelectedScheduleDays"
                  :key="day.id"
                  class="border-b border-gray-200 px-3 py-3 text-center text-xs font-medium min-w-[150px]"
                  :class="day.is_active ? 'text-gray-500' : 'text-gray-400 bg-gray-100'"
                >
                  {{ weekdayLabel(day.weekday) }}
                  <div v-if="!day.is_active" class="text-[10px] font-normal">يوم غير نشط</div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in slotGroupRows" :key="row.order" class="group align-top">
                <td
                  class="sticky right-0 z-10 bg-white group-hover:bg-gray-50 border-b border-gray-100 px-4 py-3"
                >
                  <div class="flex items-start gap-2">
                    <span class="shrink-0 px-2 py-1 rounded bg-blue-100 text-blue-800 text-xs font-bold">
                      {{ row.order }}
                    </span>
                    <div class="min-w-0">
                      <div class="text-sm font-medium text-gray-800 truncate">
                        {{ row.canonical.slot_name }}
                      </div>
                      <div class="text-xs text-gray-500">{{ slotTimingLabel(row.canonical) }}</div>
                      <div class="mt-1 flex flex-wrap gap-1">
                        <span
                          class="px-2 py-0.5 rounded text-[10px]"
                          :class="row.presentCount === sortedSelectedScheduleDays.length
                            ? 'bg-green-100 text-green-700'
                            : 'bg-amber-100 text-amber-800'"
                          :title="row.missingDayLabels.length
                            ? `غير موجود في: ${row.missingDayLabels.join('، ')}`
                            : 'موجود في كل أيام الجدول'"
                        >
                          {{ row.presentCount }}/{{ sortedSelectedScheduleDays.length }} يوم
                        </span>
                        <span class="px-2 py-0.5 rounded text-[10px] bg-gray-100 text-gray-600">
                          {{ row.studentTotal }} محجوز
                        </span>
                        <span
                          v-if="row.mixedActive"
                          class="px-2 py-0.5 rounded text-[10px] bg-amber-100 text-amber-800"
                          title="بعض الفترات في هذه المجموعة نشطة والبعض غير نشط"
                        >
                          تفعيل متفاوت
                        </span>
                      </div>
                    </div>
                  </div>
                </td>
                <td
                  v-for="day in sortedSelectedScheduleDays"
                  :key="`${row.order}-${day.id}`"
                  class="border-b border-gray-100 px-2 py-2 group-hover:bg-gray-50"
                  :class="day.is_active ? '' : 'bg-gray-50'"
                >
                  <button
                    v-if="row.cells[day.id]"
                    type="button"
                    class="w-full rounded-md border px-2 py-2 text-xs text-center transition"
                    :class="gridCellClass(row.cells[day.id])"
                    :title="gridCellTooltip(row, row.cells[day.id])"
                    @click="openSlotFromGrid(row, day)"
                  >
                    <div class="font-medium">
                      {{ row.cells[day.id].teacherCount }} معلم ·
                      {{ row.cells[day.id].studentCount }} محجوز
                    </div>
                    <div
                      v-if="row.cells[day.id].teacherNames"
                      class="mt-0.5 text-[10px] opacity-75 truncate"
                    >
                      {{ row.cells[day.id].teacherNames }}
                    </div>
                    <div v-if="!row.cells[day.id].isActive" class="mt-0.5 text-[10px]">غير نشطة</div>
                    <div v-if="row.cells[day.id].diverges" class="mt-0.5 text-[10px] font-medium">
                      ⚠ مختلفة عن المجموعة
                    </div>
                  </button>
                  <button
                    v-else
                    type="button"
                    :disabled="!canCreate"
                    class="w-full rounded-md border border-dashed border-gray-300 px-2 py-3 text-xs text-gray-400 transition"
                    :class="canCreate
                      ? 'hover:border-blue-400 hover:text-blue-600'
                      : 'cursor-default'"
                    :title="canCreate
                      ? `إضافة «${row.canonical.slot_name}» (${row.order}) إلى ${weekdayLabel(day.weekday)}`
                      : 'غير موجودة في هذا اليوم'"
                    @click="prefillSlotFromGrid(row, day)"
                  >
                    {{ canCreate ? '+' : '—' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="selectedSchedule" class="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div class="bg-white rounded-lg shadow p-4">
          <h3 class="text-lg font-semibold text-gray-900 mb-3">المعلمون المرتبطون</h3>
          <div class="flex gap-2 mb-3" v-if="canCreate || canUpdate">
            <select v-model="teacherForm.teacher_id" class="flex-1 p-2 border border-gray-300 rounded-md">
              <option value="">اختر المعلم</option>
              <option v-for="teacher in teachers" :key="teacher.id" :value="teacher.id">
                {{ teacher.fullName || teacher.full_name || `#${teacher.id}` }}
              </option>
            </select>
            <select v-model="teacherForm.role_type" class="p-2 border border-gray-300 rounded-md">
              <option value="primary">أساسي</option>
              <option value="assistant">مساعد</option>
              <option value="substitute">بديل</option>
            </select>
            <button @click="assignTeacher" class="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-md">
              ربط
            </button>
          </div>
          <div class="space-y-2">
            <div v-for="item in selectedSchedule.teachers || []" :key="item.id" class="flex items-center justify-between bg-gray-50 rounded p-2">
              <div class="text-sm">
                <div class="font-medium text-gray-800">{{ item.teacher?.full_name || item.teacher?.fullName || `#${item.teacher_id}` }}</div>
                <div class="text-gray-500 text-xs">{{ teacherRoleLabel(item.role_type) }}</div>
              </div>
              <button v-if="canDelete" @click="removeTeacher(item.teacher_id)" class="text-red-600 text-sm">إزالة</button>
            </div>
            <p v-if="!(selectedSchedule.teachers || []).length" class="text-sm text-gray-500">لا يوجد معلمون مرتبطون</p>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-4">
          <h3 class="text-lg font-semibold text-gray-900 mb-3">أيام الجدول</h3>
          <div class="flex gap-2 mb-3" v-if="canCreate || canUpdate">
            <select v-model.number="dayForm.weekday" class="flex-1 p-2 border border-gray-300 rounded-md">
              <option :value="7">الأحد</option>
              <option :value="1">الإثنين</option>
              <option :value="2">الثلاثاء</option>
              <option :value="3">الأربعاء</option>
              <option :value="4">الخميس</option>
              <option :value="5">الجمعة</option>
              <option :value="6">السبت</option>
              
            </select>
            <button @click="addDay" class="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-md">إضافة</button>
          </div>
          <div class="space-y-2">
            <div
              v-for="day in sortedSelectedScheduleDays"
              :key="day.id"
              class="flex items-center justify-between rounded p-2 border"
              :class="selectedDayId === day.id ? 'border-blue-400 bg-blue-50' : 'border-gray-200'"
            >
              <div class="flex items-center gap-3 flex-1">
                <label class="text-xs flex items-center gap-1">
                  <input
                    type="checkbox"
                    :checked="isDayBatchSelected(day.id)"
                    @change="toggleDayBatchSelection(day.id)"
                  />
                  تحديد
                </label>
                <button class="text-sm text-right flex-1" @click="selectDay(day.id)">
                  {{ weekdayLabel(day.weekday) }}
                </button>
              </div>
              <div class="flex items-center gap-3">
                <label class="text-xs flex items-center gap-2">
                  
                  <button
                    type="button"
                    role="switch"
                    :aria-checked="day.is_active"
                    dir="ltr"
                    @click.stop="toggleDayActive(day)"
                    class="relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-200"
                    :class="day.is_active ? 'bg-green-500 justify-end' : 'bg-gray-300 justify-start'"
                  >
                    <span
                      class="mx-1 inline-block h-4 w-4 rounded-full bg-white shadow-sm"
                    />
                  </button>
                </label>
                <button v-if="canDelete" @click="deleteDay(day)" class="text-red-600 text-sm">حذف</button>
              </div>
            </div>
            <p v-if="!(selectedSchedule.days || []).length" class="text-sm text-gray-500">لا توجد أيام في هذا الجدول</p>
          </div>
        </div>

        <div ref="slotPanel" class="bg-white rounded-lg shadow p-4">
          <h3 class="text-lg font-semibold text-gray-900 mb-3">فترات اليوم</h3>
          <div v-if="selectedDay || selectedDayIds.length" class="space-y-3">
            <div class="grid grid-cols-1 gap-2" v-if="canCreate || canUpdate">
              <div class="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-md p-2 text-xs text-gray-700">
                <span>
                  {{ selectedDayIds.length ? `سيتم الإضافة إلى ${selectedDayIds.length} يوم محدد` : 'سيتم الإضافة إلى اليوم المختار فقط' }}
                </span>
                <div class="flex items-center gap-2">
                  <button type="button" class="text-blue-700 hover:underline" @click="selectAllDaysForSlot">تحديد كل الأيام</button>
                  <button type="button" class="text-gray-700 hover:underline" @click="clearDaysForSlot">إلغاء التحديد</button>
                </div>
              </div>
              <input v-model="slotForm.slot_name" type="text" placeholder="اسم الفترة" class="p-2 border border-gray-300 rounded-md" />
              <div class="grid grid-cols-2 gap-2">
                <input v-model.number="slotForm.slot_order" type="number" min="1" placeholder="الترتيب" class="p-2 border border-gray-300 rounded-md" />
                <input v-model.number="slotForm.duration_minutes" type="number" min="1" placeholder="المدة بالدقائق" class="p-2 border border-gray-300 rounded-md" />
              </div>
              <div class="grid grid-cols-2 gap-2">
                <select v-model="slotForm.start_mode" class="p-2 border border-gray-300 rounded-md">
                  <option value="fixed_time">وقت ثابت</option>
                  <option value="prayer_offset">بالاعتماد على الصلاة</option>
                </select>
                <input v-if="slotForm.start_mode === 'fixed_time'" v-model="slotForm.fixed_start_time" type="time" class="p-2 border border-gray-300 rounded-md" />
                <select v-else v-model="slotForm.prayer_name" class="p-2 border border-gray-300 rounded-md">
                  <option value="fajr">الفجر</option>
                  <option value="dhuhr">الظهر</option>
                  <option value="asr">العصر</option>
                  <option value="maghrib">المغرب</option>
                  <option value="isha">العشاء</option>
                </select>
              </div>
              <input
                v-if="slotForm.start_mode === 'prayer_offset'"
                v-model.number="slotForm.offset_minutes"
                type="number"
                placeholder="الإزاحة بالدقائق"
                class="p-2 border border-gray-300 rounded-md"
              />
              <div v-if="editingSlotId" class="grid grid-cols-1 gap-2 p-2 bg-blue-50 border border-blue-100 rounded-md">
                <div class="text-xs text-blue-800">وضع التعديل</div>
                <div class="flex flex-wrap items-center gap-3 text-xs">
                  <label class="flex items-center gap-1">
                    <input v-model="slotEditApplyMode" type="radio" value="single" />
                    تعديل اليوم الحالي فقط
                  </label>
                  <label class="flex items-center gap-1" :class="selectedDayIds.length ? '' : 'text-gray-400'">
                    <input
                      v-model="slotEditApplyMode"
                      type="radio"
                      value="selected"
                      :disabled="!selectedDayIds.length"
                    />
                    تعديل الأيام المحددة ({{ selectedDayIds.length }})
                  </label>
                </div>
              </div>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  @click="editingSlotId ? saveSlotUpdate() : addSlot()"
                  class="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-md"
                >
                  {{ editingSlotId ? 'حفظ التعديل' : 'إضافة فترة' }}
                </button>
                <button
                  v-if="editingSlotId"
                  @click="cancelSlotEditing"
                  class="bg-gray-200 hover:bg-gray-300 text-gray-800 px-3 py-2 rounded-md"
                >
                  إلغاء التعديل
                </button>
              </div>
            </div>

            <div class="space-y-2">
              <div v-for="slot in selectedDay.slots || []" :key="slot.id" class="border border-gray-200 rounded p-2">
                <div class="flex items-center justify-between">
                  <div>
                    <div class="text-sm font-medium text-gray-800">{{ slot.slot_name }} ({{ slot.slot_order }})</div>
                    <div class="text-xs text-gray-500">
                      {{ slotStartModeLabel(slot.start_mode) }}
                      <span v-if="slot.start_mode === 'prayer_offset'"> ({{ prayerNameLabel(slot.prayer_name) }})</span>
                      - {{ slot.duration_minutes }} دقيقة
                    </div>
                  </div>
                  <div class="flex items-center gap-2">
                    <label class="text-xs flex items-center gap-1">
                      <input type="checkbox" :checked="slot.is_active" @change="toggleSlotActive(slot)" />
                      نشط
                    </label>
                    <button v-if="canUpdate" @click="startSlotEditing(slot)" class="text-blue-700 text-sm">تعديل</button>
                    <button v-if="canDelete" @click="deleteSlot(slot)" class="text-red-600 text-sm">حذف</button>
                  </div>
                </div>
              </div>
              <p v-if="!(selectedDay.slots || []).length" class="text-sm text-gray-500">لا توجد فترات لليوم المحدد</p>
            </div>
          </div>
          <p v-else class="text-sm text-gray-500">اختر يومًا أولًا لإدارة الفترات</p>
        </div>
      </div>

      <div v-if="showScheduleModal" class="fixed inset-0 bg-black bg-opacity-50 z-50" @click.self="closeScheduleModal">
        <div class="bg-white rounded-xl max-w-xl mx-auto mt-20 p-5">
          <h3 class="text-lg font-semibold mb-4">{{ editingScheduleId ? 'تعديل جدول الجلسات' : 'إنشاء جدول الجلسات' }}</h3>
          <div class="space-y-3">
            <input v-model="scheduleForm.title" type="text" placeholder="عنوان الجدول" class="w-full p-2 border border-gray-300 rounded-md" />
            <textarea v-model="scheduleForm.description" placeholder="الوصف" class="w-full p-2 border border-gray-300 rounded-md"></textarea>
            <div class="grid grid-cols-2 gap-2">
              <input v-model="scheduleForm.start_date" type="date" class="w-full p-2 border border-gray-300 rounded-md" />
              <input v-model="scheduleForm.end_date" type="date" class="w-full p-2 border border-gray-300 rounded-md" />
            </div>
            <div class="grid grid-cols-3 gap-2">
              <select v-model="scheduleForm.schedule_type" class="w-full p-2 border border-gray-300 rounded-md">
                <option value="mixed">مختلط</option>
                <option value="fixed">ثابت</option>
                <option value="prayer_based">مرتبط بالصلاة</option>
              </select>
              <label class="flex items-center justify-center gap-2 border border-gray-300 rounded-md">
                <input v-model="scheduleForm.is_paid" type="checkbox" />
                مدفوع
              </label>
              <label class="flex items-center justify-center gap-2 border border-gray-300 rounded-md">
                <input v-model="scheduleForm.is_active" type="checkbox" />
                نشط
              </label>
            </div>
          </div>
          <div class="flex gap-2 mt-4">
            <button @click="saveSchedule" class="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md">
              حفظ
            </button>
            <button @click="closeScheduleModal" class="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-md">
              إلغاء
            </button>
          </div>
        </div>
      </div>

      <!-- Day deletion: shows what is blocking, and lets the admin clear it -->
      <div v-if="showDeleteModal" class="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto" @click.self="closeDeleteModal">
        <div class="bg-white rounded-xl max-w-4xl mx-auto my-10 p-5">
          <div class="flex items-start justify-between mb-4">
            <div>
              <h3 class="text-lg font-semibold">{{ deleteTargetTitle }}</h3>
              <p class="text-sm text-gray-500">{{ deleteTargetSubtitle }}</p>
            </div>
            <button @click="closeDeleteModal" class="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
          </div>

          <div class="flex gap-2 border-b border-gray-200 mb-4">
            <button
              @click="deleteModalTab = 'enrollments'"
              class="px-3 py-2 text-sm border-b-2 -mb-px"
              :class="deleteModalTab === 'enrollments' ? 'border-blue-600 text-blue-700 font-medium' : 'border-transparent text-gray-500'"
            >
              الطلاب المرتبطون
              <span v-if="deleteDependents" class="text-xs">({{ deleteDependents.blockers.enrollments }})</span>
            </button>
            <button
              @click="openRecycleTab"
              class="px-3 py-2 text-sm border-b-2 -mb-px"
              :class="deleteModalTab === 'recycle' ? 'border-blue-600 text-blue-700 font-medium' : 'border-transparent text-gray-500'"
            >
              سلة المحذوفات
            </button>
          </div>

          <p v-if="dependentsLoading" class="text-sm text-gray-500 py-6 text-center">جارٍ التحميل...</p>

          <div v-else-if="deleteModalTab === 'enrollments'">
            <!-- Occurrences are real sessions with attendance behind them; they can never be cleared from here -->
            <div v-if="deleteDependents && deleteDependents.blockers.occurrences > 0" class="mb-4 p-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-900">
              <div class="font-medium">لا يمكن حذف {{ deleteTargetNoun }}</div>
              <p class="mt-1">
                يوجد {{ deleteDependents.blockers.occurrences }} جلسة مسجلة على {{ deleteTargetNoun }}
                <span v-if="deleteDependents.occurrences.first_date">
                  (من {{ deleteDependents.occurrences.first_date }} إلى {{ deleteDependents.occurrences.last_date }})
                </span>
                تحمل سجلات حضور وحجوزات. استخدم «تعطيل {{ deleteTargetNoun }}» بدلاً من الحذف للحفاظ على السجلات.
              </p>
            </div>

            <div v-if="deleteDependents && deleteDependents.can_delete" class="mb-4 p-3 rounded-md bg-green-50 border border-green-200 text-sm text-green-800">
              لا توجد ارتباطات على {{ deleteTargetNoun }}. يمكن الحذف الآن.
            </div>

            <div v-if="deleteDependents && deleteDependents.enrollments.length" class="overflow-x-auto">
              <table class="w-full text-sm">
                <thead class="bg-gray-50 text-gray-600">
                  <tr>
                    <th class="p-2 text-right font-medium">الطالب</th>
                    <th class="p-2 text-right font-medium">الفترة</th>
                    <th class="p-2 text-right font-medium">المعلم</th>
                    <th class="p-2 text-right font-medium">الحالة</th>
                    <th class="p-2 text-right font-medium">سجلات الحضور</th>
                    <th class="p-2 text-right font-medium">الإجراء</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="row in deleteDependents.enrollments" :key="row.id">
                    <tr class="border-t border-gray-100">
                      <td class="p-2">{{ row.student_name }}</td>
                      <td class="p-2">{{ row.slot_name }} <span class="text-gray-400">({{ row.slot_order }})</span></td>
                      <td class="p-2">{{ row.teacher_name }}</td>
                      <td class="p-2">
                        <span class="text-xs px-2 py-0.5 rounded-full" :class="enrollmentStatusClass(row.status)">
                          {{ enrollmentStatusLabel(row.status) }}
                        </span>
                      </td>
                      <td class="p-2">
                        <span :class="row.attendance_count > 0 ? 'text-amber-700 font-medium' : 'text-gray-400'">
                          {{ row.attendance_count }}
                        </span>
                      </td>
                      <td class="p-2">
                        <div class="flex items-center gap-2">
                          <button @click="startMove(row)" class="text-blue-700 hover:underline">نقل</button>
                          <button @click="removeEnrollment(row)" class="text-red-600 hover:underline">إزالة</button>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="movingEnrollmentId === row.id" class="bg-blue-50 border-t border-blue-100">
                      <td colspan="6" class="p-3">
                        <div class="text-xs text-blue-900 mb-2">نقل «{{ row.student_name }}» من فترة «{{ row.slot_name }}» إلى:</div>
                        <div class="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          <select v-model="moveForm.day_id" @change="moveForm.slot_teacher_id = ''" class="p-2 border border-gray-300 rounded-md text-sm">
                            <option value="">اختر اليوم</option>
                            <option v-for="d in moveTargetDays" :key="d.day_id" :value="d.day_id">
                              {{ weekdayLabel(d.weekday) }}
                            </option>
                          </select>
                          <select v-model="moveForm.slot_teacher_id" :disabled="!moveForm.day_id" class="p-2 border border-gray-300 rounded-md text-sm">
                            <option value="">اختر الفترة والمعلم</option>
                            <option v-for="t in moveTargetSlots" :key="t.id" :value="t.id">
                              {{ t.slot_name }} ({{ t.slot_order }}) — {{ t.teacher_name }}
                            </option>
                          </select>
                          <div class="flex gap-2">
                            <button
                              @click="confirmMove(row)"
                              :disabled="!moveForm.slot_teacher_id || moveSaving"
                              class="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white px-3 py-2 rounded-md text-sm"
                            >
                              تأكيد النقل
                            </button>
                            <button @click="cancelMove" class="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-3 py-2 rounded-md text-sm">
                              إلغاء
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
              <p class="text-xs text-gray-500 mt-3">
                «إزالة» تنقل ربط الطالب — ومعه سجلات حضوره — إلى سلة المحذوفات، ويمكن استعادته لاحقاً من التبويب المجاور.
              </p>
            </div>

            <p v-else-if="deleteDependents && !deleteDependents.enrollments.length" class="text-sm text-gray-500 py-4">
              لا يوجد طلاب مرتبطون بـ{{ deleteTargetNoun }}.
            </p>
          </div>

          <div v-else>
            <p v-if="recycleLoading" class="text-sm text-gray-500 py-6 text-center">جارٍ التحميل...</p>
            <div v-else-if="recycleRows.length" class="overflow-x-auto">
              <table class="w-full text-sm">
                <thead class="bg-gray-50 text-gray-600">
                  <tr>
                    <th class="p-2 text-right font-medium">الطالب</th>
                    <th class="p-2 text-right font-medium">الفترة</th>
                    <th class="p-2 text-right font-medium">سجلات الحضور</th>
                    <th class="p-2 text-right font-medium">حُذف بواسطة</th>
                    <th class="p-2 text-right font-medium">تاريخ الحذف</th>
                    <th class="p-2 text-right font-medium">الإجراء</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in recycleRows" :key="row.id" class="border-t border-gray-100">
                    <td class="p-2">{{ row.student_name }}</td>
                    <td class="p-2">{{ row.slot_name }} — {{ row.teacher_name }}</td>
                    <td class="p-2">{{ row.attendance_count }}</td>
                    <td class="p-2">{{ row.deleted_by_name || '—' }}</td>
                    <td class="p-2 text-gray-500">{{ formatDateTime(row.deleted_at) }}</td>
                    <td class="p-2">
                      <button
                        v-if="row.can_restore"
                        @click="restoreFromRecycle(row)"
                        class="text-green-700 hover:underline"
                      >
                        استعادة
                      </button>
                      <span v-else-if="!row.original_slot_exists" class="text-xs text-gray-400">
                        الفترة الأصلية محذوفة
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p v-else class="text-sm text-gray-500 py-4">سلة المحذوفات فارغة.</p>
          </div>

          <div class="flex flex-wrap gap-2 mt-5 pt-4 border-t border-gray-200">
            <button
              @click="confirmDelete"
              :disabled="!deleteDependents || !deleteDependents.can_delete || deleting"
              class="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md"
            >
              حذف {{ deleteTargetNoun }}
            </button>
            <button
              v-if="deleteTarget?.is_active"
              @click="deactivateFromModal"
              class="flex-1 bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-md"
            >
              تعطيل {{ deleteTargetNoun }}
            </button>
            <button @click="closeDeleteModal" class="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-md">
              إغلاق
            </button>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import { defineComponent } from 'vue';
import { usePermissions } from '@/composables/usePermissions';
import {
  assignIqraTeacher,
  createIqraDay,
  createIqraSchedule,
  createIqraSlot,
  deleteIqraDay,
  deleteIqraSchedule,
  deleteIqraSlot,
  deleteIqraStudentSlotEnrollment,
  getIqraDayDependents,
  getIqraSchedule,
  getIqraSlotDependents,
  listIqraEnrollmentRecycleBin,
  listIqraSchedules,
  listIqraSlotTeachers,
  moveIqraStudentSlotEnrollment,
  restoreIqraEnrollment,
  listTeachersForIqra,
  removeIqraTeacher,
  updateIqraDay,
  updateIqraSchedule,
  updateIqraSlot,
} from '@/services/iqraSchedules';

export default defineComponent({
  name: 'IqraSchedulesManagement',
  setup() {
    const permissions = usePermissions();
    return { permissions };
  },
  data() {
    return {
      loading: false,
      schedules: [],
      pagination: { current_page: 1, per_page: 20, total: 0 },
      filters: {
        q: '',
        is_active: '',
      },
      teachers: [],
      selectedSchedule: null,
      selectedDayId: null,
      selectedDayIds: [],
      showScheduleModal: false,
      editingScheduleId: null,
      scheduleForm: {
        title: '',
        description: '',
        start_date: '',
        end_date: '',
        schedule_type: 'mixed',
        is_paid: true,
        is_active: true,
      },
      teacherForm: {
        teacher_id: '',
        role_type: 'primary',
      },
      dayForm: {
        weekday: 7,
      },
      slotForm: {
        slot_name: '',
        slot_order: 1,
        start_mode: 'fixed_time',
        fixed_start_time: '',
        prayer_name: 'fajr',
        offset_minutes: 0,
        duration_minutes: 30,
      },
      editingSlotId: null,
      editingSlotSourceOrder: null,
      slotEditApplyMode: 'single',
      showDeleteModal: false,
      deleteTarget: null,
      deleteDependents: null,
      dependentsLoading: false,
      deleting: false,
      deleteModalTab: 'enrollments',
      moveTargets: [],
      movingEnrollmentId: null,
      moveSaving: false,
      moveForm: { day_id: '', slot_teacher_id: '' },
      recycleRows: [],
      recycleLoading: false,
    };
  },
  computed: {
    deleteTargetNoun() {
      return this.deleteTarget?.kind === 'slot' ? 'الفترة' : 'اليوم';
    },
    deleteTargetTitle() {
      if (!this.deleteTarget) return '';
      return this.deleteTarget.kind === 'slot'
        ? `حذف فترة «${this.deleteTarget.label}»`
        : `حذف يوم ${this.deleteTarget.label}`;
    },
    deleteTargetSubtitle() {
      if (!this.deleteTarget) return '';
      const parts = [this.selectedSchedule?.title];
      if (this.deleteTarget.kind === 'slot') parts.push(this.deleteTarget.dayLabel);
      return parts.filter(Boolean).join(' — ');
    },
    moveTargetDays() {
      // Deleting a whole day rules out every slot on it; deleting one slot
      // leaves the day's other slots as valid destinations.
      const excludedDayId = this.deleteTarget?.kind === 'day' ? Number(this.deleteTarget.id) : null;
      const days = new Map();
      this.moveTargets.forEach((target) => {
        const dayId = Number(target.day_id);
        if (dayId === excludedDayId || days.has(dayId)) return;
        days.set(dayId, { day_id: dayId, weekday: Number(target.weekday) });
      });
      return Array.from(days.values()).sort((a, b) => a.weekday - b.weekday);
    },
    moveTargetSlots() {
      if (!this.moveForm.day_id) return [];
      const excludedSlotId = this.deleteTarget?.kind === 'slot' ? Number(this.deleteTarget.id) : null;
      return this.moveTargets
        .filter((target) => Number(target.day_id) === Number(this.moveForm.day_id))
        .filter((target) => Number(target.slot_id) !== excludedSlotId)
        .sort((a, b) => Number(a.slot_order) - Number(b.slot_order));
    },
    canRead() {
      return this.permissions.hasPermission('iqra_schedules', 'read');
    },
    canCreate() {
      return this.permissions.hasPermission('iqra_schedules', 'create');
    },
    canUpdate() {
      return this.permissions.hasPermission('iqra_schedules', 'update');
    },
    canDelete() {
      return this.permissions.hasPermission('iqra_schedules', 'delete');
    },
    selectedDay() {
      if (!this.selectedSchedule || !this.selectedDayId) return null;
      return (this.selectedSchedule.days || []).find((d) => d.id === this.selectedDayId) || null;
    },
    sortedSelectedScheduleDays() {
      if (!this.selectedSchedule) return [];
      const days = [...(this.selectedSchedule.days || [])];
      return days.sort((a, b) => this.daySortWeight(a.weekday) - this.daySortWeight(b.weekday));
    },
    // One row per slot_order («الترتيب»). That number is the only thing linking the
    // same فترة across days — the multi-day slot edit below and the bulk-by-slot-group
    // enrollment endpoint both match on it — so grouping by it shows a group as a whole.
    slotGroupRows() {
      const days = this.sortedSelectedScheduleDays;
      if (!days.length) return [];

      const groups = new Map();
      days.forEach((day) => {
        (day.slots || []).forEach((slot) => {
          const order = Number(slot.slot_order);
          if (!groups.has(order)) groups.set(order, []);
          groups.get(order).push({ day, slot });
        });
      });

      const rows = [];
      groups.forEach((entries, order) => {
        // Canonical = the most common name+timing in the group; anything else is flagged.
        const tally = new Map();
        entries.forEach(({ slot }) => {
          const fingerprint = this.slotFingerprint(slot);
          const item = tally.get(fingerprint) || { fingerprint, slot, count: 0 };
          item.count += 1;
          tally.set(fingerprint, item);
        });
        const canonicalEntry = Array.from(tally.values()).sort((a, b) => b.count - a.count)[0];

        const cells = {};
        let studentTotal = 0;
        let teacherTotal = 0;
        let hasDivergence = false;
        let activeCells = 0;

        entries.forEach(({ day, slot }) => {
          const teachers = (slot.slot_teachers || []).filter((st) => st.is_active !== false);
          const studentCount = teachers.reduce(
            (sum, st) => sum + Number(st.active_enrollments_count || 0),
            0
          );
          const diverges = this.slotFingerprint(slot) !== canonicalEntry.fingerprint;
          const isActive = slot.is_active !== false;

          if (diverges) hasDivergence = true;
          if (isActive) activeCells += 1;
          studentTotal += studentCount;
          teacherTotal += teachers.length;

          cells[day.id] = {
            slot,
            dayId: day.id,
            isActive,
            diverges,
            teacherCount: teachers.length,
            studentCount,
            teacherNames: teachers
              .map((st) => st.teacher?.full_name || st.teacher?.fullName || `#${st.teacher_id}`)
              .join('، '),
          };
        });

        rows.push({
          order,
          canonical: canonicalEntry.slot,
          cells,
          presentCount: entries.length,
          studentTotal,
          teacherTotal,
          hasDivergence,
          // A group split across active and inactive slots is an inconsistency too,
          // even when every cell shares the same name and timing.
          mixedActive: activeCells > 0 && activeCells < entries.length,
          missingDayLabels: days
            .filter((day) => !cells[day.id])
            .map((day) => this.weekdayLabel(day.weekday)),
        });
      });

      // Numeric order only. Do NOT sort these by time: slot_order is an opaque group
      // label, and production has a slot with order 112 that actually runs at 18:03.
      return rows.sort((a, b) => a.order - b.order);
    },
    incompleteSlotGroupCount() {
      const total = this.sortedSelectedScheduleDays.length;
      return this.slotGroupRows.filter((row) => row.presentCount < total).length;
    },
    inconsistentSlotGroupCount() {
      return this.slotGroupRows.filter((row) => row.hasDivergence || row.mixedActive).length;
    },
  },
  async mounted() {
    await this.permissions.fetchUserPermissions();
    await this.loadTeachers();
    await this.loadSchedules();
  },
  methods: {
    async loadSchedules() {
      this.loading = true;
      try {
        const result = await listIqraSchedules({
          q: this.filters.q || undefined,
          is_active: this.filters.is_active || undefined,
          page: this.pagination.current_page,
          size: this.pagination.per_page,
        });
        this.schedules = result.data || [];
        this.pagination = result.pagination || this.pagination;

        if (this.selectedSchedule) {
          const refreshed = this.schedules.find((s) => s.id === this.selectedSchedule.id);
          this.selectedSchedule = refreshed || null;
          if (!this.selectedSchedule) this.selectedDayId = null;
        }
      } catch (error) {
        alert(error.message);
      } finally {
        this.loading = false;
      }
    },
    async loadTeachers() {
      try {
        this.teachers = await listTeachersForIqra();
      } catch (error) {
        alert(error.message);
      }
    },
    async selectSchedule(schedule) {
      try {
        const full = await getIqraSchedule(schedule.id);
        this.selectedSchedule = full || schedule;
        const firstDay = this.sortedSelectedScheduleDays[0];
        this.selectedDayId = firstDay ? firstDay.id : null;
        this.selectedDayIds = [];
      } catch (error) {
        alert(error.message);
      }
    },
    selectDay(dayId) {
      this.selectedDayId = dayId;
    },
    toggleDayBatchSelection(dayId) {
      const value = Number(dayId);
      if (this.selectedDayIds.includes(value)) {
        this.selectedDayIds = this.selectedDayIds.filter((id) => id !== value);
      } else {
        this.selectedDayIds = [...this.selectedDayIds, value];
      }
    },
    isDayBatchSelected(dayId) {
      return this.selectedDayIds.includes(Number(dayId));
    },
    selectAllDaysForSlot() {
      this.selectedDayIds = this.sortedSelectedScheduleDays.map((day) => day.id);
    },
    clearDaysForSlot() {
      this.selectedDayIds = [];
    },
    openCreateModal() {
      this.editingScheduleId = null;
      this.scheduleForm = {
        title: '',
        description: '',
        start_date: '',
        end_date: '',
        schedule_type: 'mixed',
        is_paid: true,
        is_active: true,
      };
      this.showScheduleModal = true;
    },
    openEditModal(schedule) {
      this.editingScheduleId = schedule.id;
      this.scheduleForm = {
        title: schedule.title || '',
        description: schedule.description || '',
        start_date: this.normalizeDateInput(schedule.start_date),
        end_date: this.normalizeDateInput(schedule.end_date),
        schedule_type: schedule.schedule_type || 'mixed',
        is_paid: schedule.is_paid !== false,
        is_active: !!schedule.is_active,
      };
      this.showScheduleModal = true;
    },
    closeScheduleModal() {
      this.showScheduleModal = false;
    },
    async saveSchedule() {
      try {
        if (this.editingScheduleId) {
          await updateIqraSchedule(this.editingScheduleId, this.scheduleForm);
        } else {
          await createIqraSchedule(this.scheduleForm);
        }

        this.closeScheduleModal();
        await this.loadSchedules();
      } catch (error) {
        alert(error.message);
      }
    },
    async deleteSchedule(schedule) {
      if (!confirm(`تأكيد حذف جدول الجلسات: ${schedule.title}`)) return;
      try {
        await deleteIqraSchedule(schedule.id);
        if (this.selectedSchedule?.id === schedule.id) {
          this.selectedSchedule = null;
          this.selectedDayId = null;
        }
        await this.loadSchedules();
      } catch (error) {
        alert(error.message);
      }
    },
    async assignTeacher() {
      if (!this.selectedSchedule) return;
      if (!this.teacherForm.teacher_id) {
        alert('اختر المعلم أولاً');
        return;
      }
      try {
        await assignIqraTeacher(this.selectedSchedule.id, {
          teacher_id: Number(this.teacherForm.teacher_id),
          role_type: this.teacherForm.role_type,
          is_active: true,
        });
        this.teacherForm.teacher_id = '';
        await this.refreshSelectedSchedule();
      } catch (error) {
        alert(error.message);
      }
    },
    async removeTeacher(teacherId) {
      if (!this.selectedSchedule) return;
      if (!confirm('تأكيد إزالة المعلم من جدول الجلسات؟')) return;
      try {
        await removeIqraTeacher(this.selectedSchedule.id, teacherId);
        await this.refreshSelectedSchedule();
      } catch (error) {
        alert(error.message);
      }
    },
    async addDay() {
      if (!this.selectedSchedule) return;
      try {
        await createIqraDay(this.selectedSchedule.id, {
          weekday: this.dayForm.weekday,
          is_active: true,
        });
        await this.refreshSelectedSchedule();
      } catch (error) {
        alert(error.message);
      }
    },
    async toggleDayActive(day) {
      if (!this.selectedSchedule) return;
      try {
        await updateIqraDay(this.selectedSchedule.id, day.id, {
          is_active: !day.is_active,
        });
        await this.refreshSelectedSchedule();
      } catch (error) {
        alert(error.message);
      }
    },
    // Deleting a day is blocked by anything still pointing at its slots, so the
    // button opens a modal that shows the blockers and offers a way to clear them.
    async deleteDay(day) {
      if (!this.selectedSchedule) return;
      await this.openDeleteModal({
        kind: 'day',
        id: day.id,
        label: this.weekdayLabel(day.weekday),
        is_active: day.is_active,
      });
    },
    async deleteSlot(slot) {
      if (!this.selectedDay) return;
      await this.openDeleteModal({
        kind: 'slot',
        id: slot.id,
        label: slot.slot_name,
        is_active: slot.is_active,
        dayId: this.selectedDay.id,
        dayLabel: this.weekdayLabel(this.selectedDay.weekday),
      });
    },
    async openDeleteModal(target) {
      this.deleteTarget = target;
      this.showDeleteModal = true;
      this.deleteModalTab = 'enrollments';
      this.deleteDependents = null;
      this.movingEnrollmentId = null;
      this.moveForm = { day_id: '', slot_teacher_id: '' };
      await this.loadDependents();
      await this.loadMoveTargets();
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
      this.deleteTarget = null;
      this.deleteDependents = null;
      this.movingEnrollmentId = null;
      this.recycleRows = [];
      this.moveForm = { day_id: '', slot_teacher_id: '' };
    },
    async loadDependents() {
      if (!this.selectedSchedule || !this.deleteTarget) return;
      this.dependentsLoading = true;
      try {
        this.deleteDependents = this.deleteTarget.kind === 'slot'
          ? await getIqraSlotDependents(this.deleteTarget.dayId, this.deleteTarget.id)
          : await getIqraDayDependents(this.selectedSchedule.id, this.deleteTarget.id);
      } catch (error) {
        alert(error.message);
      } finally {
        this.dependentsLoading = false;
      }
    },
    async loadMoveTargets() {
      if (!this.selectedSchedule) return;
      try {
        const rows = await listIqraSlotTeachers({ schedule_id: this.selectedSchedule.id });
        this.moveTargets = Array.isArray(rows) ? rows : (rows?.data || []);
      } catch (error) {
        this.moveTargets = [];
      }
    },
    startMove(row) {
      this.movingEnrollmentId = row.id;
      this.moveForm = { day_id: '', slot_teacher_id: '' };
    },
    cancelMove() {
      this.movingEnrollmentId = null;
      this.moveForm = { day_id: '', slot_teacher_id: '' };
    },
    async confirmMove(row) {
      if (!this.moveForm.slot_teacher_id) return;
      this.moveSaving = true;
      try {
        await moveIqraStudentSlotEnrollment(row.id, Number(this.moveForm.slot_teacher_id));
        this.cancelMove();
        await this.loadDependents();
        await this.refreshSelectedSchedule();
      } catch (error) {
        alert(error.message);
      } finally {
        this.moveSaving = false;
      }
    },
    async removeEnrollment(row) {
      const attendanceWarning = row.attendance_count > 0
        ? `\n\nتنبيه: سيتم نقل ${row.attendance_count} سجل حضور معه.`
        : '';
      const message = `إزالة «${row.student_name}» من فترة «${row.slot_name}»؟${attendanceWarning}`
        + '\n\nيمكن استعادة السجل لاحقاً من سلة المحذوفات.';
      if (!confirm(message)) return;
      try {
        await deleteIqraStudentSlotEnrollment(row.id);
        await this.loadDependents();
      } catch (error) {
        alert(error.message);
      }
    },
    async confirmDelete() {
      if (!this.deleteDependents?.can_delete || !this.selectedSchedule || !this.deleteTarget) return;
      const isSlot = this.deleteTarget.kind === 'slot';
      const message = isSlot
        ? 'تأكيد حذف الفترة؟'
        : 'تأكيد حذف يوم الجدول؟ سيتم حذف الفترات التابعة له.';
      if (!confirm(message)) return;
      const targetId = this.deleteTarget.id;
      const dayId = this.deleteTarget.dayId;
      this.deleting = true;
      try {
        if (isSlot) {
          await deleteIqraSlot(dayId, targetId);
        } else {
          await deleteIqraDay(this.selectedSchedule.id, targetId);
          if (this.selectedDayId === targetId) this.selectedDayId = null;
          this.selectedDayIds = this.selectedDayIds.filter((id) => id !== Number(targetId));
        }
        this.closeDeleteModal();
        await this.refreshSelectedSchedule();
      } catch (error) {
        alert(error.message);
      } finally {
        this.deleting = false;
      }
    },
    async deactivateFromModal() {
      if (!this.deleteTarget) return;
      const target = this.deleteTarget;
      const dependents = this.deleteDependents;
      this.closeDeleteModal();
      if (target.kind === 'slot') {
        await this.toggleSlotActive({ id: target.id, is_active: target.is_active });
      } else {
        await this.toggleDayActive({ id: target.id, is_active: target.is_active, weekday: dependents?.day?.weekday });
      }
    },
    async openRecycleTab() {
      this.deleteModalTab = 'recycle';
      await this.loadRecycleBin();
    },
    async loadRecycleBin() {
      if (!this.deleteTarget) return;
      this.recycleLoading = true;
      try {
        const scope = this.deleteTarget.kind === 'slot'
          ? { slot_id: this.deleteTarget.id }
          : { day_id: this.deleteTarget.id };
        const result = await listIqraEnrollmentRecycleBin({ ...scope, state: 'deleted', size: 200 });
        this.recycleRows = result?.data || [];
      } catch (error) {
        alert(error.message);
        this.recycleRows = [];
      } finally {
        this.recycleLoading = false;
      }
    },
    async restoreFromRecycle(row) {
      if (!confirm(`استعادة ربط «${row.student_name}» بفترة «${row.slot_name}»؟`)) return;
      try {
        const result = await restoreIqraEnrollment(row.id);
        if (result?.attendance_skipped > 0) {
          alert(`تمت الاستعادة، لكن تعذّرت استعادة ${result.attendance_skipped} سجل حضور لأن جلساتها لم تعد موجودة.`);
        }
        await this.loadRecycleBin();
        await this.loadDependents();
      } catch (error) {
        alert(error.message);
      }
    },
    enrollmentStatusLabel(status) {
      const labels = {
        active: 'نشط',
        paused: 'موقوف',
        cancelled: 'ملغي',
        completed: 'مكتمل',
      };
      return labels[status] || status;
    },
    enrollmentStatusClass(status) {
      const classes = {
        active: 'bg-green-100 text-green-800',
        paused: 'bg-yellow-100 text-yellow-800',
        cancelled: 'bg-gray-200 text-gray-700',
        completed: 'bg-blue-100 text-blue-800',
      };
      return classes[status] || 'bg-gray-100 text-gray-700';
    },
    formatDateTime(value) {
      if (!value) return '—';
      return String(value).replace('T', ' ').slice(0, 16);
    },
    async addSlot() {
      const targetDayIds = this.selectedDayIds.length
        ? this.selectedDayIds
        : this.selectedDay
          ? [this.selectedDay.id]
          : [];
      if (!targetDayIds.length) {
        alert('اختر يومًا واحدًا أو أكثر قبل إضافة الفترة');
        return;
      }
      try {
        const payload = this.buildSlotPayload();
        payload.is_active = true;

        await Promise.all(targetDayIds.map((dayId) => createIqraSlot(dayId, payload)));
        this.resetSlotForm();
        this.selectedDayIds = [];
        await this.refreshSelectedSchedule();
        alert(targetDayIds.length > 1 ? `تمت إضافة الفترة إلى ${targetDayIds.length} أيام` : 'تمت إضافة الفترة لليوم المحدد');
      } catch (error) {
        alert(error.message);
      }
    },
    startSlotEditing(slot) {
      this.editingSlotId = Number(slot.id);
      this.editingSlotSourceOrder = Number(slot.slot_order);
      this.slotEditApplyMode = 'single';
      this.slotForm.slot_name = slot.slot_name || '';
      this.slotForm.slot_order = Number(slot.slot_order) || 1;
      this.slotForm.start_mode = slot.start_mode || 'fixed_time';
      this.slotForm.fixed_start_time = String(slot.fixed_start_time || '').slice(0, 5);
      this.slotForm.prayer_name = slot.prayer_name || 'fajr';
      this.slotForm.offset_minutes = Number(slot.offset_minutes ?? 0);
      this.slotForm.duration_minutes = Number(slot.duration_minutes) || 30;
    },
    cancelSlotEditing() {
      this.editingSlotId = null;
      this.editingSlotSourceOrder = null;
      this.slotEditApplyMode = 'single';
      this.resetSlotForm();
    },
    // Name + timing only; is_active is shown by the cell's own styling and by the
    // group's mixedActive flag, so it must not count as a name/timing divergence.
    slotFingerprint(slot) {
      const prayerOffset = slot.start_mode === 'prayer_offset';
      return [
        slot.slot_name || '',
        slot.start_mode || '',
        slot.start_mode === 'fixed_time' ? String(slot.fixed_start_time || '').slice(0, 5) : '',
        prayerOffset ? slot.prayer_name || '' : '',
        prayerOffset ? String(slot.offset_minutes ?? '') : '',
        String(slot.duration_minutes ?? ''),
      ].join('|');
    },
    slotTimingLabel(slot) {
      if (!slot) return '-';
      const duration = slot.duration_minutes ? ` · ${slot.duration_minutes} دقيقة` : '';
      if (slot.start_mode === 'fixed_time') {
        const time = String(slot.fixed_start_time || '').slice(0, 5);
        return `${time || 'وقت غير محدد'}${duration}`;
      }
      const offset = Number(slot.offset_minutes ?? 0);
      const sign = offset < 0 ? '−' : '+';
      return `${this.prayerNameLabel(slot.prayer_name)} ${sign}${Math.abs(offset)} د${duration}`;
    },
    gridCellTooltip(row, cell) {
      const parts = [
        `${cell.slot.slot_name} (${row.order})`,
        this.slotTimingLabel(cell.slot),
      ];
      if (!cell.isActive) parts.push('فترة غير نشطة');
      if (cell.diverges) parts.push(`يختلف عن المجموعة (${this.slotTimingLabel(row.canonical)})`);
      if (cell.teacherNames) parts.push(`المعلمون: ${cell.teacherNames}`);
      if (this.canUpdate) parts.push('اضغط للتعديل');
      return parts.join(' — ');
    },
    gridCellClass(cell) {
      if (cell.diverges) {
        return 'border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100';
      }
      if (!cell.isActive) {
        return 'border-gray-200 bg-gray-100 text-gray-400 hover:bg-gray-200';
      }
      return 'border-green-200 bg-green-50 text-green-800 hover:bg-green-100';
    },
    // Clicking a filled cell hands the slot to the existing edit form — no new write path.
    openSlotFromGrid(row, day) {
      const cell = row.cells[day.id];
      if (!cell) return;
      this.selectDay(day.id);
      if (!this.canUpdate) return;
      this.startSlotEditing(cell.slot);
      this.scrollToSlotPanel();
    },
    // Clicking a hole prefills the add form with the group's canonical name/timing.
    prefillSlotFromGrid(row, day) {
      if (!this.canCreate) return;
      this.selectDay(day.id);
      this.selectedDayIds = [Number(day.id)];
      this.cancelSlotEditing(); // resets the form, so it must run before we fill it
      const canonical = row.canonical || {};
      this.slotForm.slot_name = canonical.slot_name || '';
      this.slotForm.slot_order = Number(row.order) || 1;
      this.slotForm.start_mode = canonical.start_mode || 'fixed_time';
      this.slotForm.fixed_start_time = String(canonical.fixed_start_time || '').slice(0, 5);
      this.slotForm.prayer_name = canonical.prayer_name || 'fajr';
      this.slotForm.offset_minutes = Number(canonical.offset_minutes ?? 0);
      this.slotForm.duration_minutes = Number(canonical.duration_minutes) || 30;
      this.scrollToSlotPanel();
    },
    scrollToSlotPanel() {
      this.$nextTick(() => {
        const el = this.$refs.slotPanel;
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    },
    buildSlotPayload() {
      const payload = {
        slot_name: this.slotForm.slot_name,
        slot_order: Number(this.slotForm.slot_order),
        start_mode: this.slotForm.start_mode,
        duration_minutes: Number(this.slotForm.duration_minutes),
      };

      if (this.slotForm.start_mode === 'fixed_time') {
        payload.fixed_start_time = this.slotForm.fixed_start_time;
      } else {
        payload.prayer_name = this.slotForm.prayer_name;
        payload.offset_minutes = Number(this.slotForm.offset_minutes);
      }

      return payload;
    },
    resetSlotForm() {
      this.slotForm.slot_name = '';
      this.slotForm.slot_order = 1;
      this.slotForm.start_mode = 'fixed_time';
      this.slotForm.fixed_start_time = '';
      this.slotForm.prayer_name = 'fajr';
      this.slotForm.offset_minutes = 0;
      this.slotForm.duration_minutes = 30;
    },
    async saveSlotUpdate() {
      if (!this.selectedDay || !this.editingSlotId) return;

      const targetDayIds = this.slotEditApplyMode === 'selected' && this.selectedDayIds.length
        ? this.selectedDayIds
        : [this.selectedDay.id];

      try {
        const payload = this.buildSlotPayload();
        const updateRequests = [];
        const missingDays = [];

        targetDayIds.forEach((dayId) => {
          const day = (this.selectedSchedule?.days || []).find((item) => Number(item.id) === Number(dayId));
          if (!day) {
            missingDays.push(String(dayId));
            return;
          }

          let targetSlot = null;
          if (Number(day.id) === Number(this.selectedDay.id)) {
            targetSlot = (day.slots || []).find((item) => Number(item.id) === Number(this.editingSlotId));
          }

          if (!targetSlot) {
            targetSlot = (day.slots || []).find(
              (item) => Number(item.slot_order) === Number(this.editingSlotSourceOrder)
            );
          }

          if (!targetSlot) {
            missingDays.push(this.weekdayLabel(day.weekday));
            return;
          }

          updateRequests.push(updateIqraSlot(day.id, targetSlot.id, payload));
        });

        if (!updateRequests.length) {
          throw new Error('تعذر العثور على الفترة المطلوب تعديلها في الأيام المحددة');
        }

        await Promise.all(updateRequests);
        const updatedCount = updateRequests.length;
        await this.refreshSelectedSchedule();
        this.cancelSlotEditing();

        if (missingDays.length) {
          alert(`تم تعديل الفترة في ${updatedCount} يوم، ولم يتم العثور عليها في: ${missingDays.join('، ')}`);
        } else {
          alert(updatedCount > 1 ? `تم تعديل الفترة في ${updatedCount} أيام` : 'تم تعديل الفترة لليوم المحدد');
        }
      } catch (error) {
        alert(error.message);
      }
    },
    async toggleSlotActive(slot) {
      if (!this.selectedDay) return;
      try {
        await updateIqraSlot(this.selectedDay.id, slot.id, { is_active: !slot.is_active });
        await this.refreshSelectedSchedule();
      } catch (error) {
        alert(error.message);
      }
    },
    async refreshSelectedSchedule() {
      if (!this.selectedSchedule) return;
      const full = await getIqraSchedule(this.selectedSchedule.id);
      this.selectedSchedule = full || this.selectedSchedule;

      if (this.selectedDayId) {
        const exists = (this.selectedSchedule.days || []).some((d) => d.id === this.selectedDayId);
        if (!exists) {
          this.selectedDayId = this.sortedSelectedScheduleDays[0]?.id || null;
        }
      } else {
        this.selectedDayId = this.sortedSelectedScheduleDays[0]?.id || null;
      }
      const allowedIds = new Set((this.selectedSchedule.days || []).map((day) => Number(day.id)));
      this.selectedDayIds = this.selectedDayIds.filter((id) => allowedIds.has(Number(id)));
      if (this.editingSlotId) {
        const selectedDaySlots = this.selectedDay?.slots || [];
        const stillExists = selectedDaySlots.some((slot) => Number(slot.id) === Number(this.editingSlotId));
        if (!stillExists) {
          this.cancelSlotEditing();
        }
      }

      await this.loadSchedules();
    },
    weekdayLabel(value) {
      const map = {
        1: 'الإثنين',
        2: 'الثلاثاء',
        3: 'الأربعاء',
        4: 'الخميس',
        5: 'الجمعة',
        6: 'السبت',
        7: 'الأحد',
      };
      return map[value] || `اليوم ${value}`;
    },
    daySortWeight(weekday) {
      const order = { 7: 0, 1: 1, 2: 2, 3: 3, 4: 4, 5: 5, 6: 6 };
      return order[weekday] ?? 99;
    },
    scheduleTypeLabel(value) {
      const map = {
        mixed: 'مختلط',
        fixed: 'ثابت',
        prayer_based: 'مرتبط بالصلاة',
      };
      return map[value] || value || '-';
    },
    teacherRoleLabel(value) {
      const map = {
        primary: 'أساسي',
        assistant: 'مساعد',
        substitute: 'بديل',
      };
      return map[value] || value || '-';
    },
    slotStartModeLabel(value) {
      const map = {
        fixed_time: 'وقت ثابت',
        prayer_offset: 'بالاعتماد على الصلاة',
      };
      return map[value] || value || '-';
    },
    prayerNameLabel(value) {
      const map = {
        fajr: 'الفجر',
        dhuhr: 'الظهر',
        asr: 'العصر',
        maghrib: 'المغرب',
        isha: 'العشاء',
      };
      return map[value] || value || '-';
    },
    formatDate(value) {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString('ar-SA');
    },
    normalizeDateInput(value) {
      if (!value) return '';
      if (typeof value === 'string') {
        return value.slice(0, 10);
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      return date.toISOString().slice(0, 10);
    },
  },
});
</script>

